export const STREETKIT_INDEX_SCHEMA_VERSION = "streetkit-index-v1" as const;
export const STREETKIT_SHARD_SCHEMA_VERSION = "streetkit-shard-v1" as const;
export const STREETKIT_DETAIL_SCHEMA_VERSION = "streetkit-detail-v1" as const;
export const STREETKIT_LINZ_SOURCE_PROVIDER = "LINZ" as const;
export const STREETKIT_LINZ_SOURCE_DATASET = "NZ Addresses" as const;
export const STREETKIT_LINZ_SOURCE_LAYER = "123113" as const;
export const STREETKIT_LINZ_SOURCE_LAYER_ID = 123113 as const;
export const STREETKIT_LINZ_SOURCE_URL = "https://data.linz.govt.nz/layer/123113" as const;
export const STREETKIT_LINZ_DATA_DICTIONARY_URL =
  "https://data.linz.govt.nz/document/25898" as const;
export const STREETKIT_LINZ_LICENCE = "CC BY 4.0" as const;
export const STREETKIT_LINZ_ATTRIBUTION =
  "Contains data sourced from the LINZ Data Service licensed for reuse under CC BY 4.0." as const;
export const STREETKIT_V1_SOURCE_LIMITATIONS = [
  "StreetKit v1 uses LINZ NZ Addresses layer 123113 for physical address autocomplete.",
  "LINZ is a source import for offline static index builds, not a runtime lookup API.",
  "StreetKit v1 is not NZ Post delivery certified and does not provide DPID, postal validation, delivered-only coverage, PO Box, Private Bag, Counter Delivery, RD/mailtown, courier deliverability, or delivery guarantees."
] as const;

export type StreetKitIndexSchemaVersion = typeof STREETKIT_INDEX_SCHEMA_VERSION;
export type StreetKitShardSchemaVersion = typeof STREETKIT_SHARD_SCHEMA_VERSION;
export type StreetKitDetailSchemaVersion = typeof STREETKIT_DETAIL_SCHEMA_VERSION;

export type StreetKitCountry = "NZ";

export type StreetKitId = string;

export type StreetKitSourceIdentity = number | string;

export type RawLinzAddressRow = {
  address_id: string;
  full_address: string;
  full_address_number: string;
  address_number: string;
  road_id?: string | null;
  change_id?: string | null;
  address_number_suffix?: string | null;
  address_number_high?: string | null;
  address_number_prefix?: string | null;
  unit?: string | null;
  unit_type?: string | null;
  unit_value?: string | null;
  level_type?: string | null;
  level_value?: string | null;
  full_road_name: string;
  full_road_name_ascii?: string | null;
  road_name_prefix?: string | null;
  road_name?: string | null;
  road_name_ascii?: string | null;
  road_type_name?: string | null;
  road_suffix?: string | null;
  suburb_locality?: string | null;
  suburb_locality_ascii?: string | null;
  town_city?: string | null;
  town_city_ascii?: string | null;
  territorial_authority?: string | null;
  territorial_authority_ascii?: string | null;
  gd2000_xcoord?: string | null;
  gd2000_ycoord?: string | null;
  longitude?: string | null;
  latitude?: string | null;
  geometry?: string | null;
  shape?: string | null;
  source_dataset?: string | null;
  address_class?: string | null;
  is_land?: string | null;
  water_name?: string | null;
  water_body_name?: string | null;
  address_lifecycle?: string | null;
  lifecycle_status?: string | null;
  status?: string | null;
  full_address_ascii?: string | null;
  postcode?: string | null;
  [field: string]: string | null | undefined;
};

export type StreetKitSourceDescriptor = {
  provider: "LINZ";
  dataset: "NZ Addresses";
  layer: "123113";
  layer_id: 123113;
  layerId?: 123113;
  sourceUrl?: string;
  source_url?: string;
  dataDictionaryUrl?: string;
  data_dictionary_url?: string;
  licence?: "CC BY 4.0";
  attribution?: string;
};

export type StreetKitIndexSourceMetadata = {
  label: string;
  fixture: boolean;
  fixture_mode: boolean;
  provider: "LINZ";
  dataset: "NZ Addresses";
  layer: "123113";
  layer_id: 123113;
  layerId: 123113;
  sourceUrl: string;
  source_url: string;
  dataDictionaryUrl: string;
  data_dictionary_url: string;
  exportedAt: string | null;
  exported_at: string | null;
  builtAt: string;
  built_at: string;
  licence: "CC BY 4.0";
  attribution: string;
  limitations: string[];
  record_count: number;
  source_record_count: number;
  exportFormat?: string | null;
  export_format?: string | null;
  sourceRevision?: string | null;
  source_revision?: string | null;
};

export type CanonicalAddressSource = StreetKitSourceDescriptor & {
  address_id: StreetKitSourceIdentity;
  change_id?: StreetKitSourceIdentity | null;
  source_url?: string;
  export_format?: string;
  exported_at?: string;
  source_revision?: string;
};

export type AddressUnitComponent = {
  type: string;
  value: string;
  display: string;
};

export type AddressLevelComponent = {
  type: string;
  value: string;
  display: string;
};

export type AddressNumberComponent = {
  prefix: string | null;
  number: number;
  suffix: string | null;
  high: number | null;
  display: string;
};

export type RoadComponent = {
  prefix: string | null;
  name: string;
  type: string | null;
  suffix: string | null;
  display: string;
};

export type CanonicalAddressComponents = {
  unit: AddressUnitComponent | null;
  level: AddressLevelComponent | null;
  address_number: AddressNumberComponent;
  road: RoadComponent;
  suburb_locality: string | null;
  town_city: string | null;
  territorial_authority: string | null;
  postcode?: string | null;
};

export type AddressDisplay = {
  label: string;
  line1: string;
  line2: string | null;
  town_city: string | null;
  postcode?: string | null;
};

export type AddressCoordinates = {
  longitude: number;
  latitude: number;
  crs: "EPSG:4167" | string;
  source_fields?: string[];
};

export type CanonicalAddressClassification = {
  source_dataset: string | null;
  address_class: string | null;
  lifecycle_status?: string | null;
  is_current: boolean;
  is_land: boolean;
  is_water: boolean;
};

export type CanonicalAddressSearch = {
  folded_text: string;
  tokens: string[];
  prefix_terms: string[];
  number_tokens: string[];
  unit_tokens: string[];
  alias_tokens: string[];
};

export type CanonicalAddress = {
  id: StreetKitId;
  country: StreetKitCountry;
  dataset_version: string;
  source: CanonicalAddressSource;
  components: CanonicalAddressComponents;
  display: AddressDisplay;
  coordinates: AddressCoordinates | null;
  classification: CanonicalAddressClassification;
  search: CanonicalAddressSearch;
};

export type CompactShardAddress = {
  id: StreetKitId;
  detailPath: string | null;
  label: string;
  primaryText: string;
  secondaryText: string | null;
  components: {
    unit: string | null;
    number: string | null;
    numberSuffix: string | null;
    numberHigh: string | null;
    street: string | null;
    streetName: string | null;
    streetType: string | null;
    suburb: string | null;
    city: string | null;
    postcode: string | null;
    territorialAuthority: string | null;
    country: StreetKitCountry;
  };
  search: {
    tokens: string[];
    aliases: string[];
    numberTokens: string[];
    unitTokens: string[];
    rangeTokens: string[];
  };
  rank: {
    base: number;
    hasUnit: boolean;
    isRange: boolean;
  };
  source: {
    provider: "LINZ";
    dataset: "NZ Addresses";
    layer: "123113";
  };
};

export type AddressDetailSource = {
  provider: "LINZ";
  dataset: "NZ Addresses";
  layer: "123113";
  version: string;
  exportedAt: string | null;
  builtAt: string | null;
  licence: "CC BY 4.0";
  attribution: string;
  addressId: string;
  changeId: string | null;
  sourceUrl: string | null;
  dataDictionaryUrl: string | null;
  duplicateSourceIds: string[];
};

export type AddressDetailComponents = {
  unit: string | null;
  unitType: string | null;
  level: string | null;
  levelType: string | null;
  number: string | null;
  numberSuffix: string | null;
  numberHigh: string | null;
  fullAddressNumber: string | null;
  street: string | null;
  streetName: string | null;
  streetType: string | null;
  streetSuffix: string | null;
  suburb: string | null;
  city: string | null;
  postcode: string | null;
  territorialAuthority: string | null;
  region: string | null;
  country: StreetKitCountry;
};

export type AddressDetailFlags = {
  isCurrent: boolean | null;
  isLand: boolean | null;
  isWater: boolean | null;
  isRange: boolean;
};

export type AddressDetailLocation = {
  lat: number | null;
  lon: number | null;
  crs: string | null;
  sourceFields: string[];
};

export type AddressDetail = {
  schemaVersion: StreetKitDetailSchemaVersion;
  schema_version: StreetKitDetailSchemaVersion;
  indexVersion: string;
  index_version: string;
  id: StreetKitId;
  label: string;
  display: {
    line1: string | null;
    line2: string | null;
    suburbLine: string | null;
    cityLine: string | null;
  };
  components: AddressDetailComponents;
  location: AddressDetailLocation;
  source: AddressDetailSource;
  flags: AddressDetailFlags;
};

export type StreetKitMeta = {
  schema_version: StreetKitIndexSchemaVersion;
  schemaVersion?: StreetKitIndexSchemaVersion;
  current_version: string;
  currentVersion?: string;
  indexVersion?: string;
  index_version?: string;
  base_url?: string;
  baseUrl?: string;
  generated_at: string;
  builtAt?: string;
  manifest_url: string;
  manifestUrl?: string;
  min_widget_version: string;
  minWidgetVersion?: string;
  access?: StreetKitAccessMetadata;
  source: StreetKitIndexSourceMetadata;
  shards: {
    strategy: "prefix-v1" | string;
    prefix_length: number;
    template: string;
    split_map_url: string | null;
    postcode_template: string | null;
  };
  detail: {
    strategy: "bucketed-detail-v1" | "per-address-v1" | string;
    bucket_strategy:
      | "streetkit-id-fnv1a-prefix-3"
      | "streetkit-id-fnv1a-prefix-4"
      | "linz-address-id-prefix-2"
      | string;
    bucketStrategy?: string;
    bucket_prefix_length?: number;
    bucketPrefixLength?: number;
    template: string;
  };
  cache: {
    meta_max_age_seconds: number;
    versioned_max_age_seconds: number;
  };
};

export type StreetKitManifestFile = {
  path: string;
  record_count: number | null;
  recordCount?: number | null;
  bytes?: number | null;
  gzip_bytes: number | null;
  gzipBytes?: number | null;
  sha256: string;
};

export type StreetKitManifest = {
  schema_version: StreetKitIndexSchemaVersion;
  schemaVersion?: StreetKitIndexSchemaVersion;
  dataset_version: string;
  indexVersion?: string;
  index_version?: string;
  generated_at: string;
  builtAt?: string;
  source: StreetKitIndexSourceMetadata & {
    export_format: string;
    exported_at: string | null;
    source_revision: string | null;
  };
  counts: {
    source_records: number | null;
    canonical_records: number | null;
    compact_records: number | null;
    duplicate_source_records: number | null;
    excluded_records: {
      lifecycle: number | null;
      water_or_non_land: number | null;
      missing_required_fields: number | null;
      invalid_coordinates: number | null;
    };
  };
  filters: {
    lifecycle_filter: "not_available" | string;
    land_filter: string;
  };
  shards: {
    strategy: "prefix-v1" | string;
    prefix_length: number;
    target_gzip_bytes: number;
    hard_gzip_bytes: number;
    files: StreetKitManifestFile[];
    split_map: string | null;
  };
  details: {
    strategy?: string;
    bucket_strategy:
      | "streetkit-id-fnv1a-prefix-3"
      | "streetkit-id-fnv1a-prefix-4"
      | "linz-address-id-prefix-2"
      | "sha256-id-prefix-2"
      | string;
    bucketStrategy?: string;
    bucket_prefix_length?: number;
    bucketPrefixLength?: number;
    count?: number;
    record_count?: number;
    recordCount?: number;
    missing_count?: number;
    missingCount?: number;
    raw_bytes?: number;
    rawBytes?: number;
    gzip_bytes?: number;
    gzipBytes?: number;
    target_gzip_bytes?: number;
    targetGzipBytes?: number;
    hard_gzip_bytes?: number;
    hardGzipBytes?: number;
    largest_detail_size?: {
      path: string | null;
      bytes: number;
      gzip_bytes: number;
      record_count: number;
    };
    largestDetailSize?: {
      path: string | null;
      bytes: number;
      gzipBytes: number;
      recordCount: number;
    };
    files: StreetKitManifestFile[];
  };
  quality: {
    test_queries_path: string;
    last_result: "passed" | "failed" | "unknown" | string;
  };
};

export type StreetKitSuggestion = {
  streetkit_id: StreetKitId;
  label: string;
  line1?: string | null;
  line2?: string | null;
  town_city?: string | null;
  territorial_authority?: string | null;
  postcode?: string | null;
  dataset_version: string;
  score: number;
};

export type StreetKitSelectedAddress = {
  id: string;
  label: string;
  components: StreetKitSelectedAddressComponents;
  location: StreetKitSelectedAddressLocation;
  source: StreetKitSelectedAddressSource;
  confidence: StreetKitSelectedAddressConfidence;
};

export type StreetKitSelectedAddressComponents = {
  unit: string | null;
  level: string | null;
  number: string | null;
  numberSuffix: string | null;
  numberHigh: string | null;
  street: string | null;
  streetName: string | null;
  streetType: string | null;
  streetSuffix: string | null;
  suburb: string | null;
  city: string | null;
  postcode: string | null;
  territorialAuthority: string | null;
  region: string | null;
  country: StreetKitCountry;
};

export type StreetKitSelectedAddressLocation = {
  lat: number | null;
  lon: number | null;
};

export type StreetKitSelectedAddressSource = {
  provider: "LINZ";
  dataset: "NZ Addresses";
  layer: "123113";
  version: string;
  exportedAt: string | null;
  builtAt: string | null;
  licence: "CC BY 4.0";
};

export type StreetKitSelectedAddressConfidence = {
  score: number | null;
  matchType: "autocomplete";
};

export type StreetKitSearchResult = StreetKitSuggestion;

export type StreetKitAccessMode = "public" | "registered";

export type StreetKitAccessMetadata = {
  mode: StreetKitAccessMode;
  siteId?: string;
  site_id?: string;
  siteRequired: boolean;
  site_required?: boolean;
  siteIdRequired?: boolean;
  site_id_required?: boolean;
  status?: "active" | "limited" | "disabled" | "rotated" | string;
  limits?: "public-community" | "public-best-effort" | "registered" | string;
  queryParam?: string | null;
  query_param?: string | null;
  appendSiteTo?: string[];
  append_site_to?: string[];
  registrationUrl?: string;
  registration_url?: string;
};

export type StreetKitBuiltInFieldKey =
  | "label"
  | "fullLabel"
  | "line1"
  | "line2"
  | "unit"
  | "number"
  | "streetNumber"
  | "street"
  | "streetName"
  | "streetType"
  | "streetSuffix"
  | "suburb"
  | "city"
  | "townCity"
  | "postcode"
  | "territorialAuthority"
  | "region"
  | "country"
  | "linzId"
  | "id"
  | "streetkitId"
  | "sourceProvider"
  | "sourceDataset"
  | "sourceLayer"
  | "sourceVersion"
  | "datasetVersion"
  | "latitude"
  | "longitude"
  | "lat"
  | "lon";

export type StreetKitFieldOverwriteMode = "empty" | "emptyOrStreetKit" | "always";

export type StreetKitFieldControl =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement;

export type StreetKitFieldTarget =
  | string
  | StreetKitFieldControl
  | {
      target: string | StreetKitFieldControl;
      overwrite?: StreetKitFieldOverwriteMode;
      clear?: boolean;
      writeReadonly?: boolean;
    };

export type StreetKitFieldMappingOptions = {
  overwrite?: StreetKitFieldOverwriteMode;
  lineMode?: "auto" | "unitInLine1" | "unitInLine2";
  clearOnInputChange?: "metadata" | "all" | "none";
};

export type StreetKitFieldMappingContext = {
  address: StreetKitSelectedAddress;
  values: Record<StreetKitBuiltInFieldKey, string>;
};

export type StreetKitCustomFieldMapping = {
  target: string | StreetKitFieldControl;
  value:
    | StreetKitBuiltInFieldKey
    | ((
        context: StreetKitFieldMappingContext
      ) => string | number | null | undefined);
  overwrite?: StreetKitFieldOverwriteMode;
  clear?: boolean;
  writeReadonly?: boolean;
};

export type StreetKitFieldMap = Partial<Record<StreetKitBuiltInFieldKey, StreetKitFieldTarget>> & {
  custom?: StreetKitCustomFieldMapping[];
};

export type StreetKitSearchCompleteStatus =
  | "success"
  | "empty"
  | "too_short"
  | "too_broad"
  | "error";

export type StreetKitSearchStartEvent = {
  rawQuery: string;
  normalizedQuery: string;
  normalisedQuery: string;
  requestId: number;
};

export type StreetKitSearchCompleteEvent = {
  rawQuery: string;
  query: string;
  normalizedQuery: string;
  normalisedQuery: string;
  requestId: number;
  status: StreetKitSearchCompleteStatus;
  results: StreetKitSearchResult[];
};

export type StreetKitClearEvent = {
  input: HTMLInputElement;
  reason: "api" | "user";
};

export type StreetKitErrorCode =
  | "invalid_config"
  | "metadata_failed"
  | "routing_failed"
  | "shard_failed"
  | "detail_failed"
  | "field_mapping_failed"
  | "selection_failed"
  | "unknown";

export type StreetKitError = {
  code: StreetKitErrorCode;
  message: string;
  status?: number;
  url?: string;
  id?: string;
  indexBaseUrl?: string;
  siteId?: string;
  shardKey?: string;
  detailPath?: string;
  requestId?: number;
  fieldKey?: string;
  selector?: string;
  reason?: string;
  cause?: unknown;
};

export type StreetKitSource = "linz" | "linz-fixture";

export type StreetKitCoordinates = {
  latitude: number;
  longitude: number;
  crs?: string;
};

export type StreetKitAddress = StreetKitSelectedAddress;

export type StreetKitShardRecord = CompactShardAddress;

export type StreetKitIndexMeta = StreetKitMeta;
